import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../i18n/I18nContext';
import { tripSessionsAPI, usersAPI, busesAPI, routesAPI } from '../../services/api';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

const BUS_ICON = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const MARKER_ICON = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const DEFAULT_CENTER = [48.8566, 2.3522];

function ReplayPlayer({ gpsPoints, attendanceEvents }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const intervalRef = useRef(null);
  const map = useMap();
  const { t } = useI18n();

  useEffect(() => {
    if (playing && gpsPoints.length > 0) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev >= gpsPoints.length - 1) {
            setPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 1000 / speed);
    }
    return () => clearInterval(intervalRef.current);
  }, [playing, speed, gpsPoints.length]);

  useEffect(() => {
    if (gpsPoints.length > 0 && currentIndex < gpsPoints.length) {
      const point = gpsPoints[currentIndex];
      map.setView([point.latitude, point.longitude], map.getZoom());
    }
  }, [currentIndex, gpsPoints, map]);

  const currentPoint = gpsPoints[currentIndex];
  const traveled = gpsPoints.slice(0, currentIndex + 1).map((p) => [p.latitude, p.longitude]);
  const remaining = gpsPoints.slice(currentIndex).map((p) => [p.latitude, p.longitude]);

  const nearbyEvents = attendanceEvents.filter((evt) => {
    if (!currentPoint) return false;
    const evtTime = new Date(evt.logged_at).getTime();
    const currentTime = new Date(gpsPoints[currentIndex]?.recorded_at).getTime();
    return Math.abs(evtTime - currentTime) < 10000;
  });

  if (!currentPoint) return null;

  return (
    <>
      <Marker position={[currentPoint.latitude, currentPoint.longitude]} icon={BUS_ICON}>
        <Popup>
          <strong>{t('replayPosition')}</strong><br />
          Step {currentIndex + 1} / {gpsPoints.length}<br />
          {new Date(currentPoint.recorded_at).toLocaleTimeString()}
        </Popup>
      </Marker>
      {traveled.length >= 2 && <Polyline positions={traveled} color="#28a745" weight={4} opacity={0.9} />}
      {remaining.length >= 2 && <Polyline positions={remaining} color="#999" weight={3} opacity={0.4} dashArray="8 8" />}
      {nearbyEvents.map((evt, i) => {
        const icon = evt.event_type === 'pickup' ? t('pickup') : t('dropoff');
        return (
          <Marker key={i} position={[parseFloat(evt.latitude), parseFloat(evt.longitude)]} icon={MARKER_ICON}>
            <Popup>
              <strong>{icon}: {evt.first_name} {evt.last_name}</strong><br />
              {new Date(evt.logged_at).toLocaleTimeString()}
            </Popup>
          </Marker>
        );
      })}
      <div style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 1000, background: '#fff', padding: '12px 20px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <button onClick={() => setCurrentIndex(0)} style={ctrlBtn}>|&lt;</button>
        <button onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))} style={ctrlBtn}>&lt;</button>
        <button onClick={() => setPlaying(!playing)} style={{ ...ctrlBtn, background: playing ? '#dc3545' : '#28a745', color: '#fff' }}>
          {playing ? 'Pause' : 'Play'}
        </button>
        <button onClick={() => setCurrentIndex(Math.min(gpsPoints.length - 1, currentIndex + 1))} style={ctrlBtn}>&gt;</button>
        <button onClick={() => setCurrentIndex(gpsPoints.length - 1)} style={ctrlBtn}>&gt;|</button>
        <span style={{ fontSize: '12px', color: '#666', minWidth: '100px' }}>{currentIndex + 1} / {gpsPoints.length}</span>
        <select value={speed} onChange={(e) => setSpeed(Number(e.target.value))} style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ddd' }}>
          <option value={0.5}>0.5x</option>
          <option value={1}>1x</option>
          <option value={2}>2x</option>
          <option value={5}>5x</option>
          <option value={10}>10x</option>
        </select>
      </div>
    </>
  );
}

const ctrlBtn = { padding: '6px 10px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', background: '#fff', fontWeight: 'bold' };
const filterInput = { width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px' };

function TripHistory() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [gpsPoints, setGpsPoints] = useState([]);
  const [attendanceEvents, setAttendanceEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // States for filters
  const [filters, setFilters] = useState({
    driver_id: '',
    bus_id: '',
    route_id: '',
    status: '',
    start_date: '',
    end_date: ''
  });
  const [drivers, setDrivers] = useState([]);
  const [buses, setBuses] = useState([]);
  const [routes, setRoutes] = useState([]);

  useEffect(() => {
    async function loadResources() {
      try {
        const [d, b, r] = await Promise.all([
          usersAPI.list(),
          busesAPI.list(),
          routesAPI.list()
        ]);
        setDrivers(d.data.filter(u => u.role === 'driver'));
        setBuses(b.data);
        setRoutes(r.data);
      } catch (err) { /* ignore */ }
    }
    loadResources();
  }, []);

  const loadTrips = async () => {
    try {
      const activeFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== '')
      );
      const { data } = await tripSessionsAPI.list(activeFilters);
      setTrips(data);
    } catch (err) { /* ignore */ }
  };

  useEffect(() => {
    loadTrips();
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleDeleteTrip = async (e, tripId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this trip and all its GPS/attendance data?')) return;
    try {
      await tripSessionsAPI.remove(tripId);
      setTrips((prev) => prev.filter((tr) => tr.id !== tripId));
      if (selectedTrip?.id === tripId) {
        setSelectedTrip(null);
        setGpsPoints([]);
        setAttendanceEvents([]);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed.');
    }
  };

  const handleEndTrip = async (e, tripId) => {
    e.stopPropagation();
    if (!window.confirm('Force end this ongoing trip?')) return;
    try {
      await tripSessionsAPI.end(tripId);
      setTrips((prev) => prev.map((tr) => tr.id === tripId ? { ...tr, status: 'completed', ended_at: new Date().toISOString() } : tr));
      if (selectedTrip?.id === tripId) {
        setSelectedTrip(prev => ({ ...prev, status: 'completed', ended_at: new Date().toISOString() }));
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to end trip.');
    }
  };

  const loadTripHistory = async (trip) => {
    setSelectedTrip(trip);
    setLoading(true);
    try {
      const [gps, att] = await Promise.all([
        tripSessionsAPI.getGps(trip.id),
        tripSessionsAPI.getAttendance(trip.id),
      ]);
      setGpsPoints(gps.data);
      setAttendanceEvents(att.data);
    } catch (err) { /* ignore */ }
    setLoading(false);
  };

  const defaultCenter = gpsPoints.length > 0
    ? [gpsPoints[0].latitude, gpsPoints[0].longitude]
    : DEFAULT_CENTER;

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '1000px', margin: '0 auto' }}>
      <Link to="/admin" style={{ color: '#007bff', textDecoration: 'none' }}>{t('backToDashboard')}</Link>
      <h1>{t('tripHistoryTitle')}</h1>

      <div style={{ marginBottom: '20px', padding: '15px', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 150px' }}>
          <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>{t('driver')}</label>
          <select name="driver_id" value={filters.driver_id} onChange={handleFilterChange} style={filterInput}>
            <option value="">{t('all')}</option>
            {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>{t('bus')}</label>
          <select name="bus_id" value={filters.bus_id} onChange={handleFilterChange} style={filterInput}>
            <option value="">{t('all')}</option>
            {buses.map(b => <option key={b.id} value={b.id}>{b.bus_number}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>{t('route')}</label>
          <select name="route_id" value={filters.route_id} onChange={handleFilterChange} style={filterInput}>
            <option value="">{t('all')}</option>
            {routes.map(r => <option key={r.id} value={r.id}>{r.route_name}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>{t('status')}</label>
          <select name="status" value={filters.status} onChange={handleFilterChange} style={filterInput}>
            <option value="">{t('all')}</option>
            <option value="ongoing">{t('ongoing')}</option>
            <option value="completed">{t('completed')}</option>
            <option value="cancelled">{t('cancelled')}</option>
          </select>
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>{t('startDate')}</label>
          <input type="datetime-local" name="start_date" value={filters.start_date} onChange={handleFilterChange} style={filterInput} />
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>{t('endDate')}</label>
          <input type="datetime-local" name="end_date" value={filters.end_date} onChange={handleFilterChange} style={filterInput} />
        </div>
        <button onClick={() => setFilters({ driver_id: '', bus_id: '', route_id: '', status: '', start_date: '', end_date: '' })} style={{ ...ctrlBtn, height: '36px', background: '#6c757d', color: '#fff' }}>{t('reset')}</button>
      </div>

      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 300px', maxHeight: '500px', overflowY: 'auto', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0' }}>
          {trips.length === 0 ? (
            <p style={{ padding: '20px', color: '#888' }}>{t('noData')}</p>
          ) : (
            trips.map((tr) => (
              <div key={tr.id} onClick={() => loadTripHistory(tr)} style={{
                padding: '12px', borderBottom: '1px solid #eee', cursor: 'pointer',
                background: selectedTrip?.id === tr.id ? '#e9ecef' : '#fff',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flex: 1 }}>
                    <strong>{tr.route_name || '---'}</strong>
                    <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', color: tr.status === 'completed' ? '#28a745' : tr.status === 'ongoing' ? '#ffc107' : '#dc3545', backgroundColor: tr.status === 'completed' ? '#d4edda' : tr.status === 'ongoing' ? '#fff3cd' : '#f8d7da' }}>{tr.status}</span>
                  </div>
                  {user?.role === 'admin' && (
                    <div style={{ display: 'flex', gap: '4px', marginLeft: '8px' }}>
                      {tr.status === 'ongoing' && (
                        <button onClick={(e) => handleEndTrip(e, tr.id)} style={{ padding: '2px 6px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>{t('complete')}</button>
                      )}
                      <button onClick={(e) => handleDeleteTrip(e, tr.id)} style={{ padding: '2px 6px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>{t('delete')}</button>
                    </div>
                  )}
                </div>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                  {tr.driver_name || '---'} | {new Date(tr.started_at).toLocaleString()}
                  {tr.ended_at && ` → ${new Date(tr.ended_at).toLocaleTimeString()}`}
                </p>
              </div>
            ))
          )}
        </div>

        <div style={{ flex: '2 1 500px' }}>
          {selectedTrip ? (
            <div>
              <h3>{selectedTrip.route_name || 'Trip'} — {selectedTrip.status}</h3>
              {loading ? (
                <p>{t('loading')}</p>
              ) : (
                <div>
                  <p style={{ fontSize: '13px', color: '#666' }}>{t('gpsPoints', { n: gpsPoints.length })} | {t('attendanceEvents', { n: attendanceEvents.length })}</p>
                  {gpsPoints.length > 0 ? (
                    <div style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                      <MapContainer center={defaultCenter} zoom={14} style={{ height: '500px', width: '100%' }}>
                        <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <ReplayPlayer gpsPoints={gpsPoints} attendanceEvents={attendanceEvents} />
                      </MapContainer>
                    </div>
                  ) : (
                    <p style={{ color: '#888', padding: '40px', textAlign: 'center' }}>{t('noGpsData')}</p>
                  )}
                  {attendanceEvents.length > 0 && (
                    <div style={{ marginTop: '15px', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                      <div style={{ padding: '12px 15px', borderBottom: '1px solid #eee', fontWeight: 'bold', fontSize: '14px', background: '#f8f9fa' }}>
                        {t('pickupDropoffEvents')} ({attendanceEvents.length})
                      </div>
                      {attendanceEvents.map((evt, i) => (
                        <div key={i} style={{ padding: '10px 15px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', background: evt.event_type === 'pickup' ? '#28a745' : '#007bff' }} />
                            <div>
                              <strong>{evt.first_name} {evt.last_name}</strong>
                              <span style={{ marginLeft: '8px', fontSize: '12px', padding: '2px 8px', borderRadius: '12px', fontWeight: 'bold', color: evt.event_type === 'pickup' ? '#155724' : '#004085', backgroundColor: evt.event_type === 'pickup' ? '#d4edda' : '#d1ecf1' }}>
                                {evt.event_type === 'pickup' ? t('pickedUp') : t('droppedOff')}
                              </span>
                            </div>
                          </div>
                          <div style={{ fontSize: '12px', color: '#888', textAlign: 'right' }}>
                            <div>{new Date(evt.logged_at).toLocaleTimeString()}</div>
                            {evt.latitude && <div>{parseFloat(evt.latitude).toFixed(5)}, {parseFloat(evt.longitude).toFixed(5)}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: '60px', textAlign: 'center', color: '#888', background: '#fff', borderRadius: '8px' }}>
              <p>{t('selectTrip')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TripHistory;
